import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {EntityType} from 'nutshell-graphql-types';
import type {
    ArchiveCustomField as ArchiveCustomFieldMutation,
    ArchiveCustomFieldVariables as ArchiveCustomFieldVariables,
    DeleteCustomFieldInput,
} from 'nutshell-graphql-types';

import ARCHIVE_CUSTOM_FIELD from '../mutations/archive-custom-field.graphql';
import GET_CUSTOM_FIELD_STAGESETS from '../queries/get-custom-field-stagesets.graphql';
import GET_CUSTOM_FIELDS from '../queries/get-custom-fields.graphql';

export const useArchiveCustomField = (): {
    archiveCustomField: (arg1: DeleteCustomFieldInput, entityType: EntityType) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [archiveCustomField, {loading, error}] = useMutation<
        ArchiveCustomFieldMutation,
        ArchiveCustomFieldVariables
    >(ARCHIVE_CUSTOM_FIELD);

    return {
        archiveCustomField: (input: DeleteCustomFieldInput, entityType: EntityType) => {
            return archiveCustomField({
                variables: {input},
                update: (proxy, {data}) => {
                    const deletedCustomField =
                        data && data.deleteCustomField && data.deleteCustomField.customField;

                    if (!deletedCustomField) return;

                    const customFields = proxy.readQuery({
                        query: GET_CUSTOM_FIELDS,
                        variables: {input: {entityType}},
                    });

                    if (!customFields) return;

                    proxy.writeQuery({
                        query: GET_CUSTOM_FIELDS,
                        variables: {input: {entityType}},
                        data: {
                            customFields: {
                                // @ts-expect-error
                                ...customFields.customFields,
                                // @ts-expect-error
                                active: customFields.customFields.active.filter(
                                    // @ts-expect-error
                                    (field) => field.id !== deletedCustomField.id
                                ),
                                archived: [
                                    deletedCustomField,
                                    // @ts-expect-error
                                    ...customFields.customFields.archived,
                                ],
                            },
                        },
                    });

                    const stagesetCustomFields = proxy.readQuery({
                        query: GET_CUSTOM_FIELD_STAGESETS,
                    });

                    if (!stagesetCustomFields) return;

                    proxy.writeQuery({
                        query: GET_CUSTOM_FIELD_STAGESETS,
                        data: {
                            // @ts-expect-error
                            stagesets: stagesetCustomFields.stagesets.map((stageset) => ({
                                ...stageset,
                                customFields: {
                                    ...stageset.customFields,
                                    shown: stageset.customFields.shown.filter(
                                        // @ts-expect-error
                                        (item) => item.customField.id !== deletedCustomField.id
                                    ),
                                    hidden: stageset.customFields.hidden.filter(
                                        // @ts-expect-error
                                        (field) => field.id !== deletedCustomField.id
                                    ),
                                },
                            })),
                        },
                    });
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
